import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Container from '../../components/Container';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';
import SEO from '../../lib/seo';
import { projectsApi, clientsApi, assetsApi, formatDate, generateId } from '../../lib/adminApi';

/**
 * Admin Projects Management Page
 * @returns {React.ReactElement} Projects management interface
 */
const AdminProjects = () => {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAssets, setProjectAssets] = useState([]);
  const [filterClientId, setFilterClientId] = useState(searchParams.get('client') || '');

  useEffect(() => {
    loadData();
  }, [filterClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [projectsData, clientsData] = await Promise.all([
        filterClientId ? projectsApi.getByClientId(filterClientId) : projectsApi.getAll(),
        clientsApi.getAll()
      ]);
      
      setProjects(projectsData);
      setClients(clientsData);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectAssets = async (projectId) => {
    try {
      const assets = await assetsApi.getByProjectId(projectId);
      setProjectAssets(assets);
    } catch (err) {
      console.error('Failed to load project assets:', err);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      await projectsApi.create({
        ...projectData,
        id: projectData.id || generateId('proj'),
      });
      await loadData();
      setShowCreateModal(false);
    } catch (err) {
      // Show user-friendly error message for duplicate IDs
      if (err.message.includes('already exists')) {
        alert(`❌ ${err.message}\n\nPlease choose a different Project ID and try again.`);
      } else {
        alert(`Failed to create project: ${err.message}`);
      }
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      await projectsApi.update(editingProject.id, projectData);
      await loadData();
      setEditingProject(null);
    } catch (err) {
      alert(`Failed to update project: ${err.message}`);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This will also delete all associated assets.')) {
      return;
    }

    try {
      await projectsApi.delete(projectId);
      await loadData();
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setProjectAssets([]);
      }
    } catch (err) {
      alert(`Failed to delete project: ${err.message}`);
    }
  };

  const handleViewProject = async (project) => {
    setSelectedProject(project);
    await loadProjectAssets(project.id);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.display_name : clientId;
  };

  const ProjectForm = ({ project, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      id: project?.id || '',
      client_id: project?.client_id || filterClientId || '',
      title: project?.title || '',
      notes: project?.notes || '',
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold mb-4">
            {project ? 'Edit Project' : 'Create New Project'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                placeholder="e.g., proj-2025-10-01"
                required
                disabled={!!project} // Can't change ID when editing
              />
              {!project && (
                <p className="mt-1 text-xs text-gray-500">
                  Must be unique. Use letters, numbers, and hyphens only.
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client
              </label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., Austin Office Complex"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="Project description or notes..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
              >
                {project ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-blue-600">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
            <p className="text-white">Loading projects...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-blue-600">
      <SEO 
        title="Manage Projects | LSAS Admin Portal"
        description="Manage client projects and deliverables"
        url="/admin/projects"
      />
      
      <Header />
      
      {/* Admin Header */}
      <section className="pt-40 pb-8 bg-brand-black text-white">
        <Container>
          <div className="flex items-center justify-between">
            <div>
              <nav className="text-sm mb-2">
                <Link to="/admin" className="text-gray-400 hover:text-white">Admin</Link>
                <span className="text-gray-400 mx-2">/</span>
                <span className="text-white">Projects</span>
              </nav>
              <h1 className="text-3xl font-bold mb-2">Manage Projects</h1>
              <p className="text-gray-300">Create and organize client projects</p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin"
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                ← Back to Dashboard
              </Link>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
              >
                Add New Project
              </button>
            </div>
          </div>
        </Container>
      </section>

      <main className="flex-grow py-8">
        <Container>
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p className="font-semibold">Error loading projects:</p>
              <p>{error}</p>
              <button 
                onClick={loadData}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="mb-6 bg-white rounded-xl shadow-card p-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Filter by Client:</label>
              <select
                value={filterClientId}
                onChange={(e) => setFilterClientId(e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.display_name}
                  </option>
                ))}
              </select>
              {filterClientId && (
                <button
                  onClick={() => setFilterClientId('')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Filter
                </button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Projects List */}
            <div className="lg:col-span-2">
              <ComponentErrorBoundary 
                fallbackMessage="Projects list temporarily unavailable"
                componentName="ProjectsList"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">
                      Projects ({projects.length})
                      {filterClientId && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          for {getClientName(filterClientId)}
                        </span>
                      )}
                    </h2>
                  </div>
                  
                  {projects.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {projects.map((project) => (
                        <div key={project.id} className="p-6 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-medium text-gray-900">
                                {project.title}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Client: {getClientName(project.client_id)}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                ID: {project.id}
                              </p>
                              {project.notes && (
                                <p className="text-sm text-gray-600 mt-2">
                                  {project.notes}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-2">
                                Created: {formatDate(project.created_at)}
                              </p>
                            </div>
                            
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewProject(project)}
                                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                View
                              </button>
                              <button
                                onClick={() => setEditingProject(project)}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteProject(project.id)}
                                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <p>No projects found</p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="mt-2 text-brand-blue-600 hover:text-brand-blue-700"
                      >
                        Create your first project
                      </button>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>

            {/* Project Details */}
            <div>
              <ComponentErrorBoundary 
                fallbackMessage="Project details temporarily unavailable"
                componentName="ProjectDetails"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">Project Details</h2>
                  </div>
                  
                  {selectedProject ? (
                    <div className="p-6">
                      <div className="mb-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {selectedProject.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1">
                          Client: {getClientName(selectedProject.client_id)}
                        </p>
                        <p className="text-sm text-gray-500 mb-1">
                          ID: {selectedProject.id}
                        </p>
                        {selectedProject.notes && (
                          <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded">
                            {selectedProject.notes}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-3">
                          Created: {formatDate(selectedProject.created_at)}
                        </p>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Assets ({projectAssets.length})
                        </h4>
                        {projectAssets.length > 0 ? (
                          <div className="space-y-2">
                            {projectAssets.map((asset) => (
                              <div key={asset.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div>
                                  <p className="text-sm font-medium">{asset.filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {asset.kind} • {asset.size_bytes ? `${Math.round(asset.size_bytes / 1024)} KB` : 'Unknown size'}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded ${
                                  asset.kind === 'video' ? 'bg-purple-100 text-purple-800' :
                                  asset.kind === 'photo' ? 'bg-green-100 text-green-800' :
                                  asset.kind === 'zip' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {asset.kind}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No assets uploaded</p>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/assets?project=${selectedProject.id}`}
                          className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded text-center hover:bg-blue-200"
                        >
                          Manage Assets
                        </Link>
                        <Link
                          to={`/admin/clients?highlight=${selectedProject.client_id}`}
                          className="flex-1 px-3 py-2 text-sm bg-green-100 text-green-700 rounded text-center hover:bg-green-200"
                        >
                          View Client
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>Select a project to view details</p>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>
          </div>
        </Container>
      </main>

      {/* Modals */}
      {showCreateModal && (
        <ProjectForm
          onSubmit={handleCreateProject}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {editingProject && (
        <ProjectForm
          project={editingProject}
          onSubmit={handleUpdateProject}
          onCancel={() => setEditingProject(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default AdminProjects;
